import { useState, useEffect } from "react";
import API_BASE_URL from "../config";

const QRImage = ({ value, size = 160 }) => {
  if (!value) return null;
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}`;
  return <img src={url} alt="QR Code" style={{ width: size, height: size }} />;
};

const today = () => new Date().toISOString().split("T")[0];

const EMPTY = {
  floor: "",
  area: "",
  countDate: today(),
  itemCode: "",
  description: "",
  breakdown: "",
  total: "",
};

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const fmtDateTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function LPNGenerate() {
  const [form, setForm]           = useState(EMPTY);
  const [generated, setGenerated] = useState(null);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try {
      const res  = await fetch(`${API_BASE_URL}api/LPNGenerate`);
      const data = await res.json();
      setHistory(data);
    } catch { /* silently ignore */ }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    setError("");
    if (!form.floor || !form.area || !form.itemCode || !form.description || !form.breakdown || !form.total) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/LPNGenerate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          floor:       form.floor,
          area:        form.area,
          countDate:   form.countDate,
          itemCode:    form.itemCode,
          description: form.description,
          breakdown:   form.breakdown,
          total:       parseInt(form.total, 10),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setGenerated(data);
      fetchHistory();
    } catch (err) {
      setError(err.message || "Generation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(generated.lpnNumber)}`;
    const win = window.open("", "_blank", "width=500,height=700");
    win.document.write(`<!DOCTYPE html><html><head><title>LPN Label - ${generated.lpnNumber}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:'Segoe UI',Arial,sans-serif; background:#f5f5f5; display:flex; justify-content:center; padding:30px; }
      .label { background:#fff; border:1px solid #e0e0e0; border-radius:12px; width:400px; padding:28px; box-shadow:0 2px 12px rgba(0,0,0,.08); }
      .lbl-header { display:flex; align-items:center; gap:12px; padding-bottom:16px; border-bottom:2px solid #1565c0; margin-bottom:18px; }
      .lbl-icon { width:42px; height:42px; background:#1565c0; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:22px; }
      .lbl-title { font-size:16px; font-weight:700; color:#1565c0; }
      .lbl-sub { font-size:11px; color:#888; margin-top:2px; }
      .qr-sec { display:flex; flex-direction:column; align-items:center; margin-bottom:18px; gap:8px; }
      .lpn-badge { font-family:monospace; font-size:12px; font-weight:700; color:#1565c0; background:#e3f2fd; padding:4px 14px; border-radius:20px; letter-spacing:.5px; }
      .qr-box { border:1px solid #e0e0e0; border-radius:8px; padding:6px; background:#fff; }
      .fields { width:100%; border-collapse:collapse; margin-bottom:14px; }
      .fields tr { border-bottom:1px solid #f0f0f0; }
      .fields tr:last-child { border-bottom:none; }
      .fk { padding:8px 4px; font-size:10px; font-weight:600; color:#90a4ae; text-transform:uppercase; letter-spacing:.5px; width:38%; }
      .fv { padding:8px 4px; font-size:13px; font-weight:600; color:#1a1a1a; text-align:right; }
      .total-box { background:#1565c0; border-radius:8px; padding:12px 16px; display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; }
      .tl { color:#fff; font-size:13px; font-weight:700; letter-spacing:.5px; }
      .tv { color:#fff; font-size:22px; font-weight:800; }
      .meta { display:flex; justify-content:space-between; font-size:10px; color:#bbb; }
      @media print { body { background:#fff; padding:0; } .label { box-shadow:none; } @page { margin:10mm; size:A5; } }
    </style></head><body>
    <div class="label">
      <div class="lbl-header">
        <div class="lbl-icon">📦</div>
        <div>
          <div class="lbl-title">LPN Generate</div>
          <div class="lbl-sub">License Plate Number · QR Label Maker</div>
        </div>
      </div>
      <div class="qr-sec">
        <span class="lpn-badge">${generated.lpnNumber}</span>
        <div class="qr-box"><img src="${qrUrl}" width="200" height="200" /></div>
      </div>
      <table class="fields">
        <tr><td class="fk">Floor / Area</td><td class="fv">${generated.floor} — AREA ${generated.area}</td></tr>
        <tr><td class="fk">Count Date</td><td class="fv">${fmtDate(generated.countDate)}</td></tr>
        <tr><td class="fk">Item Code</td><td class="fv">${generated.itemCode}</td></tr>
        <tr><td class="fk">Description</td><td class="fv">${generated.description}</td></tr>
        <tr><td class="fk">Breakdown</td><td class="fv">${generated.breakdown}</td></tr>
      </table>
      <div class="total-box">
        <span class="tl">TOTAL</span>
        <span class="tv">${generated.total}</span>
      </div>
      <div class="meta">
        <span>🕐 ${fmtDateTime(generated.createdAt)}</span>
        <span>👤 ${generated.createdBy}</span>
      </div>
    </div>
    <script>
      window.onload = function() {
        const img = document.querySelector('.qr-box img');
        if (img.complete) { window.print(); }
        else { img.onload = function() { window.print(); }; }
      };
    <\/script>
    </body></html>`);
    win.document.close();
  };

  const handleNew = () => {
    setGenerated(null);
    setForm(EMPTY);
  };

  return (
    <div style={S.page}>
      <div style={S.card}>

        {/* HEADER */}
        <div style={S.header}>
          <div style={S.headerIcon}>📦</div>
          <div>
            <div style={S.headerTitle}>LPN Generate</div>
            <div style={S.headerSub}>License Plate Number · QR Label Maker</div>
          </div>
        </div>

        {!generated ? (
          <div style={S.body}>
            {[
              { label: "Floor",       name: "floor",       placeholder: "e.g. 1ST FLOOR" },
              { label: "Area",        name: "area",        placeholder: "e.g. 151" },
              { label: "Item Code",   name: "itemCode",    placeholder: "e.g. 1073P" },
              { label: "Description", name: "description", placeholder: "e.g. PROMO D/N" },
              { label: "Breakdown",   name: "breakdown",   placeholder: "e.g. 36 X 4" },
            ].map(({ label, name, placeholder }) => (
              <FormRow key={name} label={label}>
                <input name={name} value={form[name]} onChange={handleChange}
                  placeholder={placeholder} style={S.input} />
              </FormRow>
            ))}

            <FormRow label="Count Date">
              <input type="date" name="countDate" value={form.countDate}
                onChange={handleChange} style={S.input} />
            </FormRow>

            <FormRow label="Total">
              <input type="number" name="total" value={form.total}
                onChange={handleChange} placeholder="e.g. 144" style={S.input} />
            </FormRow>

            {error && <div style={S.error}>{error}</div>}

            <button onClick={handleGenerate} disabled={loading} style={S.btnPrimary}>
              {loading ? "Generating…" : "⚡ Generate LPN"}
            </button>
          </div>
        ) : (
          <div style={S.body}>
            <div style={S.labelCard}>
              {/* Label header */}
              <div style={S.labelHeader}>
                <div style={S.labelIconBox}>📦</div>
                <div>
                  <div style={S.labelTitle}>LPN Generate</div>
                  <div style={S.labelSubtitle}>License Plate Number · QR Label Maker</div>
                </div>
              </div>

              {/* QR + badge */}
              <div style={S.qrSection}>
                <span style={S.lpnBadge}>{generated.lpnNumber}</span>
                <div style={S.qrBox}>
                  <QRImage value={generated.lpnNumber} size={160} />
                </div>
              </div>

              {/* Fields */}
              <table style={S.fieldsTable}>
                <tbody>
                  {[
                    ["Floor / Area",  `${generated.floor} — AREA ${generated.area}`],
                    ["Count Date",    fmtDate(generated.countDate)],
                    ["Item Code",     generated.itemCode],
                    ["Description",   generated.description],
                    ["Breakdown",     generated.breakdown],
                  ].map(([k, v]) => (
                    <tr key={k} style={S.fieldRow}>
                      <td style={S.fieldKey}>{k}</td>
                      <td style={S.fieldVal}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total */}
              <div style={S.totalBox}>
                <span style={S.totalLabel}>TOTAL</span>
                <span style={S.totalVal}>{generated.total}</span>
              </div>

              {/* Meta */}
              <div style={S.metaRow}>
                <span>🕐 {fmtDateTime(generated.createdAt)}</span>
                <span>👤 {generated.createdBy}</span>
              </div>
            </div>

            <div style={S.actionRow}>
              <button onClick={handlePrint} style={S.btnOutline}>🖨 Print PDF</button>
              <button onClick={handleNew}   style={S.btnPrimary}>＋ New LPN</button>
            </div>
          </div>
        )}
      </div>

      {/* HISTORY */}
      {history.length > 0 && (
        <div style={S.histCard}>
          <div style={S.histTitle}>Recent LPN Records</div>
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["LPN Number","Floor","Area","Item Code","Description","Total","Created At","Created By"].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} style={S.tr}>
                    <td style={S.tdMono}>{r.lpnNumber}</td>
                    <td style={S.td}>{r.floor}</td>
                    <td style={S.td}>{r.area}</td>
                    <td style={S.td}>{r.itemCode}</td>
                    <td style={S.td}>{r.description}</td>
                    <td style={{ ...S.td, textAlign:"right", fontWeight:700 }}>{r.total}</td>
                    <td style={S.td}>{fmtDateTime(r.createdAt)}</td>
                    <td style={S.td}>{r.createdBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const FormRow = ({ label, children }) => (
  <div style={S.formRow}>
    <label style={S.formLabel}>{label}</label>
    {children}
  </div>
);

const S = {
  page: {
    minHeight: "100vh",
    background: "#f0f4f8",
    padding: "32px 16px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  card: {
    maxWidth: 480,
    margin: "0 auto 24px",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 2px 16px rgba(0,0,0,.10)",
    overflow: "hidden",
  },
  header: {
    background: "#1565c0",
    color: "#fff",
    padding: "18px 24px",
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  headerIcon:  { fontSize: 30 },
  headerTitle: { fontSize: 18, fontWeight: 700 },
  headerSub:   { fontSize: 11, opacity: 0.75, marginTop: 2 },
  body:        { padding: 24 },
  formRow:     { marginBottom: 14 },
  formLabel: {
    display: "block", fontSize: 11, fontWeight: 600,
    color: "#607d8b", textTransform: "uppercase",
    letterSpacing: 0.7, marginBottom: 4,
  },
  input: {
    width: "100%", boxSizing: "border-box",
    padding: "9px 12px", border: "1.5px solid #cfd8dc",
    borderRadius: 8, fontSize: 14, color: "#1a1a1a",
    outline: "none", background: "#fff",
  },
  error: {
    background: "#ffebee", color: "#c62828",
    borderRadius: 8, padding: "10px 14px",
    fontSize: 13, marginBottom: 14,
  },
  btnPrimary: {
    width: "100%", padding: "12px",
    background: "#1565c0", color: "#fff",
    border: "none", borderRadius: 10,
    fontSize: 15, fontWeight: 700,
    cursor: "pointer",
  },
  labelCard: {
    border: "1px solid #e0e0e0",
    borderRadius: 12,
    padding: "22px 22px 18px",
    background: "#fff",
    boxShadow: "0 1px 8px rgba(0,0,0,.05)",
  },
  labelHeader: {
    display: "flex", alignItems: "center", gap: 10,
    paddingBottom: 14,
    borderBottom: "2px solid #1565c0",
    marginBottom: 16,
  },
  labelIconBox: {
    width: 38, height: 38,
    background: "#1565c0",
    borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 20,
  },
  labelTitle:    { fontSize: 14, fontWeight: 700, color: "#1565c0" },
  labelSubtitle: { fontSize: 10, color: "#90a4ae", marginTop: 2 },
  qrSection: {
    display: "flex", flexDirection: "column",
    alignItems: "center", gap: 8, marginBottom: 16,
  },
  lpnBadge: {
    fontFamily: "monospace", fontSize: 12, fontWeight: 700,
    color: "#1565c0", background: "#e3f2fd",
    padding: "4px 14px", borderRadius: 20, letterSpacing: 0.5,
  },
  qrBox: {
    border: "1px solid #e0e0e0", borderRadius: 8,
    padding: 6, background: "#fff",
  },
  fieldsTable: { width: "100%", borderCollapse: "collapse", marginBottom: 14 },
  fieldRow:    { borderBottom: "1px solid #f5f5f5" },
  fieldKey: {
    padding: "7px 4px", fontSize: 10, fontWeight: 600,
    color: "#90a4ae", textTransform: "uppercase", letterSpacing: 0.5,
    width: "38%",
  },
  fieldVal: {
    padding: "7px 4px", fontSize: 13, fontWeight: 600,
    color: "#1a1a1a", textAlign: "right",
  },
  totalBox: {
    background: "#1565c0", borderRadius: 8,
    padding: "11px 16px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 12,
  },
  totalLabel: { color: "#fff", fontSize: 13, fontWeight: 700, letterSpacing: 0.5 },
  totalVal:   { color: "#fff", fontSize: 22, fontWeight: 800 },
  metaRow: {
    display: "flex", justifyContent: "space-between",
    fontSize: 10, color: "#bdbdbd",
  },
  actionRow: { display: "flex", gap: 12, marginTop: 16 },
  btnOutline: {
    flex: 1, padding: "11px", background: "#fff",
    border: "1.5px solid #1565c0", color: "#1565c0",
    borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  histCard: {
    maxWidth: 900, margin: "0 auto", background: "#fff",
    borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,.07)",
    padding: "20px 24px",
  },
  histTitle: { fontSize: 14, fontWeight: 700, color: "#1565c0", marginBottom: 14 },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    background: "#e3f2fd", padding: "9px 12px", textAlign: "left",
    fontWeight: 700, color: "#1565c0", whiteSpace: "nowrap",
  },
  tr:     { borderBottom: "1px solid #f5f5f5" },
  td:     { padding: "8px 12px", color: "#455a64", whiteSpace: "nowrap" },
  tdMono: {
    padding: "8px 12px", color: "#1565c0", fontWeight: 700,
    fontFamily: "monospace", whiteSpace: "nowrap",
  },
};